/**
 * Monitor Store — preview/program state, transitions
 */
import { createStore } from 'zustand/vanilla';
import type { TransitionType } from '@graphicsim/shared-types';
import { eventBus } from '../engine/eventBus';
import { projectStore } from './projectStore';

export type ProgramOutputType = 'none' | 'ndi' | 'hdmi' | 'watchfolder' | 'pngsequence' | 'obs' | 'virtualwebcam';

export interface MonitorStoreState {
  previewItemId: string | null;
  programItemIds: string[];
  transitionType: TransitionType;
  transitionDuration: number;
  monitorLevel: number;
  monitorEnabled: boolean;
  isTransitioning: boolean;
  programOutputType: ProgramOutputType;
  hdmiDevice: number;
  // Actions
  setPreview: (itemId: string | null) => void;
  takeToProgram: () => void;
  clearProgram: () => void;
  removeFromProgram: (itemId: string) => void;
  setTransitionType: (type: TransitionType) => void;
  setTransitionDuration: (ms: number) => void;
  setMonitorLevel: (level: number) => void;
  setMonitorEnabled: (enabled: boolean) => void;
  setTransitioning: (v: boolean) => void;
  sendItemToProgram: (itemId: string) => void;
  setProgramOutput: (type: ProgramOutputType) => void;
  setHdmiDevice: (n: number) => void;
}

export const monitorStore = createStore<MonitorStoreState>((set, get) => ({
  previewItemId: null,
  programItemIds: [],
  transitionType: 'cut',
  transitionDuration: 500,
  monitorLevel: 4,
  monitorEnabled: true,
  isTransitioning: false,
  programOutputType: 'none',
  hdmiDevice: 1,

  setPreview: (itemId) => {
    set({ previewItemId: itemId });
    eventBus.emit('monitor:previewChanged', { itemId });
  },

  takeToProgram: () => {
    const { previewItemId, programItemIds } = get();
    if (!previewItemId) return;
    if (!programItemIds.includes(previewItemId)) {
      set({ programItemIds: [...programItemIds, previewItemId] });
    }
    const item = projectStore.getState().items.find((i) => i.id === previewItemId);
    eventBus.emit('monitor:takeToProgram', { itemId: previewItemId, mediaType: item?.mediaType });
  },

  clearProgram: () => {
    set({ programItemIds: [] });
    eventBus.emit('monitor:clearProgram', {});
  },

  removeFromProgram: (itemId) => {
    set({ programItemIds: get().programItemIds.filter((id) => id !== itemId) });
    eventBus.emit('monitor:removedFromProgram', { itemId });
  },

  setTransitionType: (type) => {
    set({ transitionType: type });
    eventBus.emit('monitor:transitionChanged', { type });
  },

  setTransitionDuration: (ms) => set({ transitionDuration: ms }),

  setMonitorLevel: (level) => {
    set({ monitorLevel: Math.max(1, Math.min(7, level)) });
    eventBus.emit('monitor:levelChanged', { level });
  },

  setMonitorEnabled: (enabled) => set({ monitorEnabled: enabled }),

  setTransitioning: (v) => set({ isTransitioning: v }),

  sendItemToProgram: (itemId) => {
    const { programItemIds } = get();
    if (!programItemIds.includes(itemId)) {
      set({ programItemIds: [...programItemIds, itemId] });
    }
    const item = projectStore.getState().items.find((i) => i.id === itemId);
    eventBus.emit('monitor:takeToProgram', { itemId, mediaType: item?.mediaType });
  },

  setProgramOutput: (type) => {
    set({ programOutputType: type, monitorEnabled: type !== 'none' });
  },

  setHdmiDevice: (n) => set({ hdmiDevice: n }),
}));
